import {
	faCircle,
	faCircleInfo,
	faDesktop,
	faExclamationTriangle,
	faMobile,
	faPhone,
	faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Dialog,
	DialogPanel,
	DialogTitle,
	Radio,
	RadioGroup,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import type {
	RingCentralDeviceViewModel,
	RingCentralDialMethod,
	RingCentralUserDevice,
	RingCentralUserDevicesResult,
} from '@supply-mover-2-0/core';
import {
	chooseDefaultDialMethod,
	getRingCentralDeviceView,
	pickInitialCallOutDevice,
	RingCentralQueryService,
} from '@supply-mover-2-0/core';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Button } from '../forms/Button';
import { Checkbox } from '../forms/Checkbox';
import { Input } from '../forms/Input';

export interface RingCentralDialResult {
	dialMethod: 'callout' | 'ringout';
	// For CallOut
	deviceId?: string;
	deviceName?: string;
	// For RingOut
	ringOutPhoneNumber?: string;
	remember: boolean;
}

export interface RingCentralDialModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSelect: (result: RingCentralDialResult) => void;
	/**
	 * The dial method the user remembered. Null (the default) picks it
	 * automatically once their phones are known: CallOut when one of them can be
	 * rung right now, RingOut otherwise.
	 */
	initialDialMethod?: RingCentralDialMethod | null;
	/** The RingOut "ring me first" number to prefill */
	initialRingOutPhone?: string;
	/** The remembered CallOut phone, preselected when it can be rung */
	initialDeviceId?: string | null;
	/**
	 * Called when RingCentral needs the user to sign in again. The caller opens
	 * the RingCentral sign-in and refreshes the dial status afterwards.
	 */
	onReconnect?: () => void;
}

// Shown when the device request itself fails (network, Workd API error), so
// the dialog reports a real problem instead of "no phones" (PD-16786).
const DEVICE_LOOKUP_FAILED: RingCentralUserDevicesResult = {
	devices: [],
	appOnly: false,
	error: {
		code: 'UNAVAILABLE',
		message: 'Could not load your RingCentral phones. Please try again.',
	},
};

export function RingCentralDialModal({
	isOpen,
	onClose,
	onSelect,
	initialDialMethod = null,
	initialRingOutPhone = '',
	initialDeviceId = null,
	onReconnect,
}: RingCentralDialModalProps): React.ReactElement {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// Dial method state
	const [dialMethod, setDialMethod] = useState<RingCentralDialMethod>(
		initialDialMethod ?? 'callout',
	);

	// CallOut state. `deviceView` is null until the first lookup finishes.
	const [isLoadingDevices, setIsLoadingDevices] = useState(false);
	const [deviceView, setDeviceView] =
		useState<RingCentralDeviceViewModel | null>(null);
	const [selectedDevice, setSelectedDevice] =
		useState<RingCentralUserDevice | null>(null);
	const [loadAttempt, setLoadAttempt] = useState(0);

	// RingOut state
	const [ringOutPhone, setRingOutPhone] = useState(initialRingOutPhone);
	const [phoneError, setPhoneError] = useState<string | null>(null);

	// Shared state
	const [rememberChoice, setRememberChoice] = useState(false);

	// The dialog stays mounted between opens, so each open has to start from
	// the caller's current values. This runs only on open/close on purpose: a
	// status refresh while the dialog is open must not overwrite what the user
	// typed.
	useEffect(() => {
		if (isOpen) {
			setRingOutPhone(initialRingOutPhone);
			setPhoneError(null);
		} else {
			setDeviceView(null);
			setSelectedDevice(null);
			setPhoneError(null);
		}
	}, [isOpen]);

	// Look the user's phones up every time the dialog opens, whichever method
	// it starts on: the answer decides the automatic method, and an expired
	// RingCentral sign-in has to be caught before either method is tried.
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		let cancelled = false;

		const loadDevices = async (): Promise<void> => {
			setIsLoadingDevices(true);

			let result: RingCentralUserDevicesResult;

			try {
				result = await RingCentralQueryService.getUserDevices();
			} catch (err) {
				console.error('Failed to load RingCentral devices:', err);
				result = DEVICE_LOOKUP_FAILED;
			}

			if (cancelled) {
				return;
			}

			const view = getRingCentralDeviceView(result);

			setDeviceView(view);
			setSelectedDevice(
				view.kind === 'devices'
					? pickInitialCallOutDevice(view.devices, initialDeviceId)
					: null,
			);
			setDialMethod(chooseDefaultDialMethod(initialDialMethod, view));
			setIsLoadingDevices(false);
		};

		void loadDevices();

		return (): void => {
			cancelled = true;
		};
	}, [isOpen, loadAttempt, initialDialMethod, initialDeviceId]);

	const handleRetry = useCallback((): void => {
		setLoadAttempt((attempt) => attempt + 1);
	}, []);

	const handleUseRingOut = useCallback((): void => {
		setDialMethod('ringout');
	}, []);

	const validatePhoneNumber = useCallback(
		(phone: string): boolean => {
			const digits = phone.replace(/\D/g, '');

			if (digits.length < 10) {
				setPhoneError(
					t('Please enter a valid phone number with at least 10 digits'),
				);

				return false;
			}

			setPhoneError(null);

			return true;
		},
		[t],
	);

	const handleConfirm = useCallback((): void => {
		if (dialMethod === 'callout') {
			if (selectedDevice == null || !selectedDevice.supportsCallOut) {
				return;
			}

			onSelect({
				dialMethod: 'callout',
				deviceId: selectedDevice.deviceId,
				deviceName: selectedDevice.name,
				remember: rememberChoice,
			});
		} else {
			if (!validatePhoneNumber(ringOutPhone)) {
				return;
			}

			// Clean the phone number
			const cleanedPhone = ringOutPhone.startsWith('+')
				? `+${ringOutPhone.slice(1).replace(/\D/g, '')}`
				: ringOutPhone.replace(/\D/g, '');

			onSelect({
				dialMethod: 'ringout',
				ringOutPhoneNumber: cleanedPhone,
				remember: rememberChoice,
			});
		}
	}, [
		dialMethod,
		selectedDevice,
		ringOutPhone,
		rememberChoice,
		validatePhoneNumber,
		onSelect,
	]);

	const handlePhoneChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>): void => {
			setRingOutPhone(e.target.value);

			if (phoneError != null) {
				setPhoneError(null);
			}
		},
		[phoneError],
	);

	const getDeviceIcon = useCallback(
		(device: RingCentralUserDevice): React.ReactNode => {
			if (device.type === 'SoftPhone') {
				return (
					<FontAwesomeIcon
						icon={faDesktop}
						className={colors.classes.textSecondary}
					/>
				);
			}

			if (device.type === 'Mobile') {
				return (
					<FontAwesomeIcon
						icon={faMobile}
						className={colors.classes.textSecondary}
					/>
				);
			}

			return (
				<FontAwesomeIcon
					icon={faPhone}
					className={colors.classes.textSecondary}
				/>
			);
		},
		[colors.classes.textSecondary],
	);

	const getStatusColor = useCallback(
		(status: string): string => {
			if (status === 'Online') {
				return 'text-success';
			}

			if (status === 'Offline') {
				return 'text-faint';
			}

			return colors.classes.textMuted;
		},
		[colors.classes.textMuted],
	);

	const isReconnect = deviceView?.kind === 'reconnect';
	const appOnly =
		deviceView?.kind === 'noCallOutDevice' ? deviceView.appOnly : false;

	const canConfirm =
		deviceView != null &&
		!isReconnect &&
		(dialMethod === 'callout'
			? selectedDevice != null && selectedDevice.supportsCallOut
			: ringOutPhone.trim().length > 0);

	const ringOutButton = (
		<Button variant="outline" size="sm" onClick={handleUseRingOut}>
			{t('Use RingOut instead')}
		</Button>
	);

	const renderNotice = (
		title: string,
		body: string,
		actions: React.ReactNode,
	): React.ReactElement => (
		<div className={`rounded-lg p-4 ${colors.classes.calloutWarning}`}>
			<div className="flex items-start gap-3">
				<FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
				<div className="space-y-2">
					<p className="font-medium">{title}</p>
					<p className="text-sm">{body}</p>
					<div className="flex flex-wrap gap-2 pt-1">{actions}</div>
				</div>
			</div>
		</div>
	);

	const renderSpinner = (label: string): React.ReactElement => (
		<div className="flex flex-col items-center justify-center py-6">
			<FontAwesomeIcon
				icon={faSpinner}
				className="animate-spin text-2xl mb-3"
				style={{ color: colors.values.primaryDark }}
			/>
			<p className={colors.classes.textSecondary}>{label}</p>
		</div>
	);

	const renderDeviceList = (
		devices: Array<RingCentralUserDevice>,
	): React.ReactElement => (
		<RadioGroup
			value={selectedDevice}
			onChange={setSelectedDevice}
			className="space-y-2 max-h-48 overflow-y-auto"
		>
			{devices.map((device) => (
				<Radio
					key={device.deviceId}
					value={device}
					disabled={!device.supportsCallOut}
					className={({ checked, focus, disabled }) =>
						`relative flex rounded-lg px-4 py-3 border ${
							checked
								? `border-2 ${colors.classes.borderPrimary}`
								: colors.classes.borderDefault
						} ${focus ? 'ring-2 ring-offset-2' : ''} ${
							disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
						} ${colors.classes.bgSecondary}`
					}
				>
					{({ checked }) => (
						<div className="flex w-full items-center justify-between">
							<div className="flex items-center gap-3">
								<div className="w-8 h-8 flex items-center justify-center">
									{getDeviceIcon(device)}
								</div>
								<div>
									<p className={`font-medium ${colors.classes.textPrimary}`}>
										{t(device.name)}
									</p>
									<div className="flex items-center gap-2 text-xs">
										<span className={colors.classes.textMuted}>
											{t(device.type)}
										</span>
										<span className={colors.classes.textMuted}>{t('•')}</span>
										<span
											className={`flex items-center gap-1 ${getStatusColor(device.status)}`}
										>
											<FontAwesomeIcon icon={faCircle} className="text-[6px]" />
											{t(device.status)}
										</span>
										{device.phoneNumber != null && (
											<>
												<span className={colors.classes.textMuted}>
													{t('•')}
												</span>
												<span className={colors.classes.textMuted}>
													{t(device.phoneNumber)}
												</span>
											</>
										)}
									</div>
								</div>
							</div>
							{checked && (
								<div
									className="shrink-0 text-lg"
									style={{
										color: colors.values.primaryDark,
									}}
								>
									{t('✓')}
								</div>
							)}
						</div>
					)}
				</Radio>
			))}
		</RadioGroup>
	);

	const renderCallOutContent = (): React.ReactNode => {
		if (deviceView == null || isLoadingDevices) {
			return renderSpinner(t('Loading devices...'));
		}

		switch (deviceView.kind) {
			case 'unavailable':
				return renderNotice(
					t('Your phones could not be loaded'),
					t(deviceView.message),
					<>
						<Button variant="outline" size="sm" onClick={handleRetry}>
							{t('Try again')}
						</Button>
						{ringOutButton}
					</>,
				);

			case 'permissionDenied':
				return renderNotice(
					t('RingCentral did not allow Workd to see your phones'),
					t(deviceView.message),
					ringOutButton,
				);

			case 'noCallOutDevice':
				return renderNotice(
					deviceView.appOnly
						? t("The RingCentral App can't be dialed directly")
						: t('No phone to call from'),
					deviceView.appOnly
						? t(
								'CallOut only works with a desk phone or the older RingCentral Phone app. With RingOut, RingCentral rings your phone first and then connects the call.',
							)
						: t(
								"CallOut needs a desk phone or the older RingCentral Phone app, and RingCentral has none set up for you. The RingCentral App can't be dialed directly. With RingOut, RingCentral rings your phone first and then connects the call.",
							),
					ringOutButton,
				);

			case 'devices':
				return (
					<div className="space-y-3">
						{deviceView.hasOnlineDevice
							? null
							: renderNotice(
									t('None of your phones are online'),
									t(
										'Turn on your desk phone or sign in to the RingCentral Phone app and try again, or use RingOut instead.',
									),
									<>
										<Button variant="outline" size="sm" onClick={handleRetry}>
											{t('Try again')}
										</Button>
										{ringOutButton}
									</>,
								)}
						{renderDeviceList(deviceView.devices)}
					</div>
				);

			default:
				return null;
		}
	};

	const renderReconnect = (message: string): React.ReactElement => (
		<div className="flex flex-col items-center text-center py-4 space-y-4">
			<FontAwesomeIcon
				icon={faExclamationTriangle}
				className={`text-2xl ${colors.classes.textWarning}`}
			/>
			<p className={colors.classes.textPrimary}>{t(message)}</p>
			{onReconnect != null ? (
				<Button variant="primary" onClick={onReconnect}>
					{t('Reconnect RingCentral')}
				</Button>
			) : null}
		</div>
	);

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-50" onClose={onClose}>
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-black bg-opacity-25" />
				</TransitionChild>

				<div className="fixed inset-0 overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4 text-center">
						<TransitionChild
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<DialogPanel
								className={`w-full max-w-lg transform rounded-lg ${colors.classes.bgPrimary} text-left align-middle shadow-xl transition-all`}
							>
								{/* Header */}
								<div
									className={`px-6 py-4 border-b ${colors.classes.borderDefault}`}
								>
									<DialogTitle
										as="h3"
										className={`text-lg font-medium leading-6 ${colors.classes.textPrimary}`}
									>
										{t('Make a Call')}
									</DialogTitle>
								</div>

								{/* Content */}
								<div className="px-6 py-4 space-y-6">
									{deviceView == null ? (
										renderSpinner(t('Loading your RingCentral phones...'))
									) : deviceView.kind === 'reconnect' ? (
										renderReconnect(deviceView.message)
									) : (
										<>
											{/* Dial Method Selection */}
											<div>
												<label
													className={`block text-sm font-medium mb-2 ${colors.classes.textPrimary}`}
												>
													{t('Call Method')}
												</label>
												<RadioGroup
													value={dialMethod}
													onChange={setDialMethod}
													className="flex gap-4"
												>
													<Radio
														value="callout"
														className={({ checked }) =>
															`flex-1 cursor-pointer rounded-lg px-4 py-3 border ${
																checked
																	? `border-2 ${colors.classes.borderPrimary}`
																	: colors.classes.borderDefault
															} ${colors.classes.bgSecondary}`
														}
													>
														{({ checked }) => (
															<div className="flex items-center justify-between">
																<div>
																	<p
																		className={`font-medium ${colors.classes.textPrimary}`}
																	>
																		{t('CallOut')}
																	</p>
																	<p
																		className={`text-xs ${colors.classes.textMuted}`}
																	>
																		{t(
																			'Call from your desk phone or RingCentral Phone app',
																		)}
																	</p>
																</div>
																{checked && (
																	<span
																		style={{ color: colors.values.primaryDark }}
																	>
																		{t('✓')}
																	</span>
																)}
															</div>
														)}
													</Radio>

													<Radio
														value="ringout"
														className={({ checked }) =>
															`flex-1 cursor-pointer rounded-lg px-4 py-3 border ${
																checked
																	? `border-2 ${colors.classes.borderPrimary}`
																	: colors.classes.borderDefault
															} ${colors.classes.bgSecondary}`
														}
													>
														{({ checked }) => (
															<div className="flex items-center justify-between">
																<div>
																	<p
																		className={`font-medium ${colors.classes.textPrimary}`}
																	>
																		{t('RingOut')}
																	</p>
																	<p
																		className={`text-xs ${colors.classes.textMuted}`}
																	>
																		{t('Ring your phone, then connect')}
																	</p>
																</div>
																{checked && (
																	<span
																		style={{ color: colors.values.primaryDark }}
																	>
																		{t('✓')}
																	</span>
																)}
															</div>
														)}
													</Radio>
												</RadioGroup>
											</div>

											{/* CallOut: Device Selection */}
											{dialMethod === 'callout' && (
												<div>
													<label
														className={`block text-sm font-medium mb-2 ${colors.classes.textPrimary}`}
													>
														{t('Select Device')}
													</label>
													{renderCallOutContent()}
												</div>
											)}

											{/* RingOut: Phone Number Input */}
											{dialMethod === 'ringout' && (
												<div>
													{appOnly ? (
														<div
															className={`flex items-start gap-2 rounded-lg p-3 mb-3 text-sm ${colors.classes.calloutInfo}`}
														>
															<FontAwesomeIcon
																icon={faCircleInfo}
																className="mt-0.5"
															/>
															<p>
																{t(
																	"The RingCentral App can't be dialed directly, so RingCentral will ring the number below first and then connect you.",
																)}
															</p>
														</div>
													) : null}
													<label
														className={`block text-sm font-medium mb-2 ${colors.classes.textPrimary}`}
													>
														{t('Your Phone Number')}
													</label>
													<p
														className={`text-sm mb-3 ${colors.classes.textSecondary}`}
													>
														{t(
															'Enter the phone number where you want to receive the call. RingCentral will call you first, then connect you to the destination.',
														)}
													</p>
													<Input
														type="tel"
														value={ringOutPhone}
														onChange={handlePhoneChange}
														placeholder={t('(555) 123-4567')}
														error={phoneError != null}
														autoFocus
													/>
													{phoneError != null && (
														<p className="text-sm text-danger mt-1">
															{phoneError}
														</p>
													)}
												</div>
											)}

											{/* Remember Choice */}
											<div className="flex items-center gap-2">
												<Checkbox
													checked={rememberChoice}
													onChange={setRememberChoice}
													label={t('Remember my choices')}
												/>
											</div>

											<p className={`text-xs ${colors.classes.textMuted} italic`}>
												{t(
													'You can reset your choices in your Profile settings under Call Preferences.',
												)}
											</p>
										</>
									)}
								</div>

								{/* Footer */}
								<div
									className={`flex justify-end gap-2 px-6 py-4 border-t ${colors.classes.borderDefault}`}
								>
									<Button variant="outline" onClick={onClose}>
										{t('Cancel')}
									</Button>
									<Button
										variant="primary"
										onClick={handleConfirm}
										disabled={!canConfirm || isLoadingDevices}
									>
										{t('Make Call')}
									</Button>
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
